import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import Plotly from 'plotly.js-dist-min';
import {msg} from '@lit/localize';
import store from '../../store';
import {
	selectMaxUsableBatteryCapacity,
	selectSolarDimmProfilePlotDataBySeason,
	selectSolarLuminarCountValue,
	selectSolarLuminarPower,
	selectSolarMaxLumen,
	selectSolarPowerRequirementsBySeason,
	selectSolarPVMaxInput,

} from '../solarSelectors';

@customElement('solar-dimm-profile')
export class solarDimmProfile extends connect(store)(LitElement) {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
		}

		.solarDimmProfile {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
		}

		@media (max-width: 1366px) {
			.solarDimmProfile {
				flex-direction: column;
			}
		}

		.values {
			font: var(--font-widget-title);
			color: var(--color-white);
			line-height: 1.4;
		}

		.label {
			color: var(--color-grey4);
		}

		.debug {
			margin: 0 0 1rem 0;
			font: var(--font-widget-title);
			color: #006000;
		}

		.warn {
			color: #c16d00;
		}

		.overflow {
			color: #f10202
		}

		.debug {
			display: none;
		}

		.debug.visible {
			display: block;
		}
	`;

	plotOptions = {
		showSendToCloud: false,
		displayModeBar: false,
		showlegend: false,
		staticPlot: true,
		scrollZoom: false
	};
	plotLayout = {
		xaxis: {
			mirror: false,
			type: 'category',
			showgrid: false,
			color: '#939393',
			tickcolor: '#939393',
			ticks: 'outside',
			ticklen: 1,
			showticklabels: true,
			tickvals: ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'],
			ticktext: ['', '12h', '', '', '', '', '', '', '', '', '', '', '0h', '', '', '', '', '', '', '', '', '', '12h', '']
		},
		yaxis: {
			showgrid: false,
			tickcolor: '#fff',
			ticks: '',
			showticklabels: false,
			autorange: false,
			range: [0, 1]
		},
		width: 168,
		height: 80,
		margin: {
			t: 0,
			b: 30,
			r: 0,
			l: 0
		},
		autosize: false,
		font: {
			color: '#fff',
		},
		paper_bgcolor: 'transparent',
		plot_bgcolor: 'transparent',
	};
	plotData = null;

	@property({type: String})
	season = 'winter';

	@property({type: Number})
	generators = 1;

	@property({type: String})
	title = '';

	constructor() {
		super();

		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.solar = state.solar.data;
		this.translations = state.productData.translations;
		this.debug = state.status.debug;

		this.plotData = selectSolarDimmProfilePlotDataBySeason(state)[this.season];
		this.powerRequirement = selectSolarPowerRequirementsBySeason(state)[this.season];
		this.maxPVInput = selectSolarPVMaxInput(state)[this.generators - 1][this.season];
		this.maxUsableBatteryCapcity = selectMaxUsableBatteryCapacity(state);
		this.usableInput = Math.min(this.maxPVInput, this.maxUsableBatteryCapcity);
		this.usedInputFactor = this.powerRequirement / this.usableInput;
		this.restrictionDimmingFactor = Math.min(1, 1 / this.usedInputFactor);
		this.lumCount = selectSolarLuminarCountValue(state) ?? 1;
		this.maxLumenDimmed = Math.round(selectSolarMaxLumen(state) * this.restrictionDimmingFactor);
		this.luminarPower = Math.round(selectSolarLuminarPower(state) * this.restrictionDimmingFactor * 10) / 10;
		if (this.lumCount > 1) {
			if (this.luminarPower > selectSolarLuminarPower(state)/this.lumCount) this.luminarPower = selectSolarLuminarPower(state)/this.lumCount;
			if (this.maxLumenDimmed > selectSolarMaxLumen(state)/this.lumCount) this.maxLumenDimmed = selectSolarMaxLumen(state)/this.lumCount;
		}
		this.southernHemisphare = state.solar.data?.project?.location?.latitude < 0;
		this.seasonNames = this.southernHemisphare ? {
			winter: {
				de: 'Sommer (max)',
				en: 'Summer (max)',
				fr: 'Été (max)'
			},
			spring: {
				de: 'Früh./Her. (max)',
				en: 'Spring/Aut. (max)',
				fr: 'Prin./Aut. (max)'
			},
			summer: {
				de: 'Winter (max)',
				en: 'Winter (max)',
				fr: 'L\'hiver (max)'
			}
		} : {
			winter: {
				de: 'Winter (max)',
				en: 'Winter (max)',
				fr: 'L\'hiver (max)'
			},
			spring: {
				de: 'Früh./Her. (max)',
				en: 'Spring/Aut. (max)',
				fr: 'Prin./Aut. (max)'
			},
			summer: {
				de: 'Sommer (max)',
				en: 'Summer (max)',
				fr: 'Été (max)'
			}
		};

	}

	firstUpdated() {
		this.plot(false);
	}

	updated() {
		this.plot(false);
	}

	plot(redraw) {
		const plotElement = this.shadowRoot.getElementById('plot');
		if (plotElement && this.plotData) {
			const dimmedPlotData = JSON.parse(JSON.stringify(this.plotData));
			dimmedPlotData[0].y = dimmedPlotData[0].y.map(y => y * this.restrictionDimmingFactor);

			// dimming due to PV capacity and battery capacity
			Plotly.newPlot(plotElement,
				dimmedPlotData,
				this.plotLayout,
				this.plotOptions
			);
		}
	}

	render() {
		const label = this.title === '' ? this.seasonNames[this.season][this.lang] : this.title;

		return html`
			<div class="solarDimmProfile">
				<div class="values">
					<span class="label">${label}</span><br>
					${msg('Power')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.luminarPower}W<br>
					${msg('Lumen')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${Math.round(this.maxLumenDimmed)}lm
				</div>
				<div class="plot" id="plot"></div>
			</div>
			<div class="debug ${classMap({
				visible: this.debug,
				overflow: this.restrictionDimmingFactor < 1,
				warn: this.usedInputFactor < 1 && this.usedInputFactor > 0.70
			})}">
				pv: ${Math.round(this.maxPVInput)} Wh, battery: ${Math.round(this.maxUsableBatteryCapcity)} Wh, req.:
				${Math.round(this.powerRequirement)} Wh<br>
				used capacity: ${Math.round(this.usedInputFactor * 100)} % -> dimming level: ${Math.round(this.restrictionDimmingFactor * 100)} %<br>
			</div>
		`;

	}
}
